import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../common/ui/Button';
import { Input } from '../common/ui/Input';
import { Turnstile } from '@marsidev/react-turnstile';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../utils/posthogUtils';
import { VerificationCodeInput } from './VerificationCodeInput';
import { BannerShapes } from '../common/icons';

type ResetStep = 'email' | 'verification' | 'password';

interface PasswordRequirements {
  minLength: boolean;
  hasNumber: boolean;
  hasLowercase: boolean;
  hasUppercase: boolean;
}

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<ResetStep>('email');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements>({
    minLength: false,
    hasNumber: false,
    hasLowercase: false,
    hasUppercase: false,
  });
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const [signInCaptchaToken, setSignInCaptchaToken] = useState<string | undefined>();
  // const turnstileRef = useRef<TurnstileInstance>(null); // If Turnstile is used directly

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  const validatePassword = (value: string) => {
    setPasswordRequirements({
      minLength: value.length >= 8,
      hasNumber: /\d/.test(value),
      hasLowercase: /[a-z]/.test(value),
      hasUppercase: /[A-Z]/.test(value),
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(120); // 2 minutes
  };

  const handleSendResetEmail = async () => {
    setLoading(true);
    setError(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
        captchaToken
      });

      if (resetError) throw resetError;

      setStep('verification');
      startResendTimer();
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'auth_reset_password',
          email: email,
          action: 'send_reset_email'
        }
      });
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulVerification = () => {
    setStep('password');
    setLoading(false);
    setError(null); // Clear any previous errors on success
  };

  const handleFailedVerification = (errorMessage: string) => {
    setError(errorMessage);
    captureAppException(new Error(errorMessage), { // Pass an Error object
      tags: {
        context: 'auth_reset_password',
        email: email,
        action: 'verify_reset_code'
      }
    });
    setLoading(false);
  };

  const handleUpdatePassword = async () => {
    if (!Object.values(passwordRequirements).every(Boolean)) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      // Sign in with new password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken: signInCaptchaToken },
      });

      if (signInError) throw signInError;

      navigate('/');
    } catch (err) {
      // Handle weak password error specifically
      if (err instanceof Error && err.name === 'AuthWeakPasswordError') {
        // Capture as PostHog event instead of exception
        captureCustomEvent('password_reset_weak_password_attempt', {
          eventProperties: {
            email: email,
            error_type: 'weak_password'
          }
        });
        setError('The password you\'ve chosen meets our requirements but has been identified as commonly used and may be easy to guess. For your security, please select a different password.');
      } else {
        // Handle other errors normally
        captureAppException(err, {
          tags: {
            context: 'auth_reset_password',
            email: email,
            action: 'update_password'
          }
        });
        setError(err instanceof Error ? err.message : 'Failed to update password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1628] flex flex-col">
      <div className="absolute inset-0 pointer-events-none">
        <BannerShapes className="w-full h-full px-10" />
      </div>
      {/* Header */}
      <header className="z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img
                src="/holito-logo-white.svg"
                alt="holito"
                className="h-8"
              />
            </div>
            <Link
              to="/"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 z-10">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Reset Password</h1>
            <p className="text-gray-600 mt-2">
              {step === 'email' && 'Enter your email to reset your password'}
              {step === 'verification' && 'Enter the verification code'}
              {step === 'password' && 'Create a new password'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {step === 'email' && (
            <div className="space-y-6">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                isRequired
              />

              <Turnstile
                siteKey="0x4AAAAAAA5Qj30yypsP-5e5"
                onSuccess={(token: string) => {
                  setCaptchaToken(token)
                }}
              />

              <Button
                onClick={handleSendResetEmail}
                loading={loading}
                disabled={!email}
                className="w-full"
              >
                Send Reset Code
              </Button>

              <div className="text-center text-sm text-gray-600">
                Remember your password?{' '}
                <Link to="/" className="text-blue-600 hover:text-blue-700">
                  Login instead
                </Link>
              </div>
            </div>
          )}

          {step === 'verification' && (
            <VerificationCodeInput
              email={email}
              onResend={handleSendResetEmail}
              loading={loading}
              resendDisabled={resendDisabled}
              resendTimer={resendTimer}
              submitButtonText="Verify Code"
              otpType="recovery"
              captchaToken={captchaToken}
              onVerificationSuccess={handleSuccessfulVerification}
              onVerificationError={handleFailedVerification}
            />
          )}

          {step === 'password' && (
            <div className="space-y-6">
              <div>
                <Input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="New Password"
                  isRequired
                />

                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">Password must contain</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${passwordRequirements.minLength ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm text-gray-600">Min 8 characters</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${passwordRequirements.hasNumber ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm text-gray-600">Numbers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${passwordRequirements.hasLowercase ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm text-gray-600">Lowercase</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${passwordRequirements.hasUppercase ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm text-gray-600">Uppercase</span>
                    </div>
                  </div>
                </div>
              </div>

              <Turnstile
                siteKey="0x4AAAAAAA5Qj30yypsP-5e5"
                onSuccess={(token: string) => {
                  setSignInCaptchaToken(token)
                }}
                onError={() => {
                  setError("Failed to load CAPTCHA. Please refresh and try again.");
                  setSignInCaptchaToken(undefined);
                }}
              />

              <Button
                onClick={handleUpdatePassword}
                loading={loading}
                disabled={!Object.values(passwordRequirements).every(Boolean) || !signInCaptchaToken}
                className="w-full"
              >
                Update Password
              </Button>
            </div>
          )}
        </div>
      </div>

      <footer className="text-center p-6 text-gray-400 text-sm">
        <div className="mb-2">
          Copyright © {new Date().getFullYear()} holito |{' '}
          <Link to="https://holito.app/terms/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">
            Terms of Service
          </Link>{' '}
          |{' '}
          <a href="https://holito.app/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
};